import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Quote, PhoneForwarded, Split, Clock } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

function CountUp({ value, duration = 1500, delay = 0, prefix = "", suffix = "", reduce }) {
  const [d, setD] = useState(reduce ? value : 0);
  useEffect(() => {
    if (reduce) {
      setD(value);
      return;
    }
    let raf;
    let started = null;
    const begin = performance.now() + delay;
    const tick = (now) => {
      if (now < begin) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (started === null) started = now;
      const t = Math.min((now - started) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setD(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, delay, reduce]);
  return (
    <>
      {prefix}
      {Math.round(d).toLocaleString("en-US")}
      {suffix}
    </>
  );
}

const STATS = [
  { num: 1700, unit: "calls", label: "answered in the first 7 days · Richmond Vona", count: true },
  { num: 70, prefix: "~", unit: "hrs", label: "given back to the team, per firm each week", count: true },
  { static: "2–3", suffix: "×", label: "more qualified leads reaching attorneys", count: false },
  { num: 50000, suffix: "+", label: "callers handled across firms to date", count: true },
];

const TESTIMONIALS = [
  {
    quote:
      "Seventeen hundred calls in the first week — that's about seventy hours my team simply got back.",
    name: "Harry Nalbandyan",
    role: "Founder & Partner",
  },
  {
    quote:
      "It answers the way our front desk should, and it gets a little sharper every single week.",
    name: "Josh Kwartler",
    role: "COO · Kwartler Manus",
  },
  {
    quote:
      "Nothing slips through after hours anymore. Every caller gets a real answer, day or night.",
    name: "Lizzy DePalma",
    role: "Ops Lead · Richmond Vona",
  },
];

const CHIPS = [
  { figure: "~50%", rest: "of calls routed past the front desk", Icon: PhoneForwarded },
  { figure: "1 in 4", rest: "adjuster calls auto-routed to the right attorney", Icon: Split },
  { figure: "24/7", rest: "— never a missed after-hours call again", Icon: Clock },
];

const FIRMS = [
  "Richmond Vona",
  "Ricci",
  "Kwartler Manus",
  "LNN Law",
  "Peiffer Wolf",
  "Simmons Hanly Conroy",
  "Sokolove",
  "Laborde Earles",
];

const initials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");

export const ProofSlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [ti, setTi] = useState(0);
  const hoverRef = useRef(false);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      if (!hoverRef.current) setTi((i) => (i + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [reduce]);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  const t = TESTIMONIALS[ti];

  return (
    <Stage testId={`slide-${slide?.n ?? 7}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(20px,4%,44px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Proof · in production</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            08 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Real firms. Real numbers.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2">
          Not projections — what firms are seeing with Lexafide live, today.
        </motion.p>

        {/* Hero stats */}
        <motion.div
          variants={item}
          className="grid grid-cols-2 min-[900px]:grid-cols-4 gap-x-6 gap-y-5 mt-6"
        >
          {STATS.map((s, i) => (
            <div key={i} className="border-l border-hairline pl-4" data-testid={`stat-${i + 1}`}>
              <div className="font-display font-semibold text-3xl sm:text-[2.4rem] leading-none text-ink tabular-nums">
                {s.count ? (
                  <CountUp
                    value={s.num}
                    prefix={s.prefix || ""}
                    delay={300 + i * 150}
                    reduce={reduce}
                  />
                ) : (
                  s.static
                )}
                <span className="text-accent">
                  {s.suffix || ""}
                  {s.unit ? ` ${s.unit}` : ""}
                </span>
              </div>
              <div className="font-sans text-[12px] text-ink-soft leading-snug mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Middle */}
        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-2 gap-6 mt-7">
          {/* Testimonial */}
          <motion.div
            variants={item}
            data-testid="testimonial"
            onMouseEnter={() => (hoverRef.current = true)}
            onMouseLeave={() => (hoverRef.current = false)}
            className="flex flex-col justify-center"
          >
            <Quote className="w-9 h-9 text-accent/70 shrink-0" fill="currentColor" />
            <div className="mt-3 min-h-[96px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={ti}
                  initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduce ? 0 : -8 }}
                  transition={{ duration: reduce ? 0.001 : 0.45, ease: EASE }}
                >
                  <p
                    data-testid="testimonial-quote"
                    className="font-display text-xl sm:text-2xl leading-snug text-ink max-w-xl"
                  >
                    {t.quote}
                  </p>
                  <div className="flex items-center gap-3 mt-5">
                    <span className="w-9 h-9 rounded-full bg-accent/12 border border-accent/25 text-accent font-semibold text-[13px] flex items-center justify-center">
                      {initials(t.name)}
                    </span>
                    <div>
                      <div className="font-sans text-sm font-medium text-ink">{t.name}</div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-muted">
                        {t.role}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex items-center gap-2 mt-6">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-bar-${i + 1}`}
                  onClick={() => setTi(i)}
                  aria-label={`Show testimonial ${i + 1}`}
                  aria-current={i === ti}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === ti ? "w-8 bg-accent" : "w-4 bg-hairline hover:bg-ink-muted/50"
                  }`}
                />
              ))}
            </div>
          </motion.div>

          {/* Chips */}
          <motion.div variants={item} className="flex flex-col justify-center gap-3">
            {CHIPS.map((c, i) => (
              <div
                key={i}
                data-testid={`chip-${i + 1}`}
                className="flex items-center gap-3.5 rounded-xl border border-hairline bg-[#FBFAF6] px-4 py-3.5"
              >
                <span className="w-9 h-9 rounded-full bg-accent/12 flex items-center justify-center shrink-0">
                  <c.Icon className="w-4 h-4 text-accent" />
                </span>
                <p className="font-sans text-[14px] text-ink-soft leading-snug">
                  <span className="font-mono font-medium text-accent">{c.figure}</span> {c.rest}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div variants={item} className="border-t border-hairline pt-4 mt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mb-2.5">
            Trusted across PI, mass tort &amp; employment
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {FIRMS.map((f, i) => (
              <React.Fragment key={f}>
                {i > 0 ? <span className="w-px h-3.5 bg-hairline" /> : null}
                <span className="font-display text-[15px] text-ink-soft">{f}</span>
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default ProofSlide;
